import React from 'react';
import { classNames } from '../../../../utils';
import { ModelGradientProps } from '../ModelGradient.types';

export function TextModelGradient({ colors, rotate, animate, scale }: Omit<ModelGradientProps, 'gradient'>) {
    const uniqueId = React.useMemo(() => Math.random().toString(36).substr(2, 9), []);

    let scaleValue = 1;
    if (scale && scale !== 'none') {
        scaleValue = {
            s: 1.6,
            m: 2,
            l: 2.4,
            xl: 2.8,
        }[scale];
    }

    const getY1Value = (index: number) => {
        switch (index) {
            case 0:
                return '24.6448';
            case 1:
                return '82.8861';
            case 2:
                return '39.2052';
            case 3:
                return '68.3258';
            case 4:
                return '53.7661';
            case 5:
                return '53.7649';
            default:
                return '0';
        }
    };

    const getY2Value = (index: number) => {
        switch (index) {
            case 0:
                return '5.99988';
            case 1:
                return '101.531';
            case 2:
                return '20.5602';
            case 3:
                return '86.9707';
            case 4:
                return '35.1212';
            case 5:
                return '72.4098';
            default:
                return '0';
        }
    };

    return (
        <div
            className={classNames(
                'text-model-gradient',
                animate === 'hover' && 'text-model-gradient-animate-hover',
                animate === 'always' && 'text-model-gradient-animate-always'
            )}
            style={{
                transform: `rotate(${rotate || 0}deg) scale(${scaleValue})`,
                width: '100%',
                height: '100%',
                background: `linear-gradient(180deg, ${colors[0]} 0%,${colors[2]} 50%, ${colors[1]} 100%)`,
                borderRadius: '12px',
            }}
        >
            <svg width="100%" height="100%" viewBox="0 0 155 107" fill="none" preserveAspectRatio="none">
                <g filter={`url(#filter0_f_${uniqueId})`}>
                    <path
                        d="M142.827 5.99988C144.422 5.99988 145.229 7.92085 144.112 9.05988L129.365 24.1048C129.027 24.4502 128.563 24.6448 128.08 24.6448L27.928 24.6448C27.4443 24.6448 26.9811 24.4502 26.6425 24.1048L11.8956 9.05988C10.7791 7.92085 11.586 5.99988 13.181 5.99988L142.827 5.99988Z"
                        fill={`url(#paint0_linear_${uniqueId})`}
                    />
                </g>
                <g filter={`url(#filter1_f_${uniqueId})`}>
                    <path
                        d="M13.1782 101.531C11.5832 101.531 10.7762 99.6101 11.8927 98.4711L26.6397 83.4261C26.9782 83.0808 27.4415 82.8861 27.9251 82.8861L128.077 82.8861C128.561 82.8861 129.024 83.0808 129.362 83.4261L144.109 98.4711C145.226 99.6101 144.419 101.531 142.824 101.531L13.1782 101.531Z"
                        fill={`url(#paint1_linear_${uniqueId})`}
                    />
                </g>
                <g filter={`url(#filter2_f_${uniqueId})`}>
                    <path
                        d="M153.871 20.5602C155.534 20.5602 156.308 22.6214 155.056 23.7155L137.843 38.7605C137.515 39.0472 137.094 39.2052 136.658 39.2052L19.3436 39.2052C18.908 39.2052 18.4871 39.0472 18.1591 38.7605L0.945831 23.7155C-0.305969 22.6214 0.46785 20.5602 2.1304 20.5602L153.871 20.5602Z"
                        fill={`url(#paint2_linear_${uniqueId})`}
                    />
                </g>
                <g filter={`url(#filter3_f_${uniqueId})`}>
                    <path
                        d="M2.13347 86.9707C0.470925 86.9707 -0.302891 84.9095 0.9489 83.8154L18.1621 68.7705C18.4902 68.4838 18.911 68.3258 19.3467 68.3258L136.661 68.3258C137.097 68.3258 137.518 68.4838 137.846 68.7705L155.059 83.8154C156.311 84.9095 155.537 86.9707 153.874 86.9707L2.13347 86.9707Z"
                        fill={`url(#paint3_linear_${uniqueId})`}
                    />
                </g>
                <path
                    d="M162.783 35.1212C164.494 35.1212 165.24 37.2832 163.893 38.3381L144.688 53.3831C144.371 53.6312 143.981 53.7661 143.578 53.7661L12.4219 53.7661C12.0195 53.7661 11.6287 53.6312 11.3119 53.3831L-7.89328 38.3381C-9.23999 37.2832 -8.49399 35.1212 -6.78325 35.1212L162.783 35.1212Z"
                    fill={`url(#paint4_linear_${uniqueId})`}
                />
                <path
                    d="M-6.78342 72.4098C-8.49415 72.4098 -9.24016 70.2478 -7.89345 69.1928L11.3117 54.1479C11.6285 53.8997 12.0193 53.7649 12.4218 53.7649L143.578 53.7649C143.98 53.7649 144.371 53.8997 144.688 54.1479L163.893 69.1928C165.24 70.2478 164.494 72.4098 162.783 72.4098L-6.78342 72.4098Z"
                    fill={`url(#paint5_linear_${uniqueId})`}
                />

                <defs>
                    <filter
                        id={`filter0_f_${uniqueId}`}
                        x="4.17744"
                        y="-1.20012"
                        width="147.653"
                        height="33.0449"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                    >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feGaussianBlur stdDeviation="3.6" result="effect1_foregroundBlur" />
                    </filter>
                    <filter
                        id={`filter1_f_${uniqueId}`}
                        x="4.17451"
                        y="75.6862"
                        width="147.653"
                        height="33.0449"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                    >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feGaussianBlur stdDeviation="3.6" result="effect1_foregroundBlur" />
                    </filter>
                    <filter
                        id={`filter2_f_${uniqueId}`}
                        x="-1.11334"
                        y="19.1202"
                        width="158.228"
                        height="21.5249"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                    >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feGaussianBlur stdDeviation="0.72" result="effect1_foregroundBlur" />
                    </filter>
                    <filter
                        id={`filter3_f_${uniqueId}`}
                        x="-1.11017"
                        y="66.8858"
                        width="158.228"
                        height="21.5249"
                        filterUnits="userSpaceOnUse"
                        color-interpolation-filters="sRGB"
                    >
                        <feFlood flood-opacity="0" result="BackgroundImageFix" />
                        <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
                        <feGaussianBlur stdDeviation="0.72" result="effect1_foregroundBlur" />
                    </filter>

                    {[...Array(6)].map((_, i) => (
                        <linearGradient
                            key={i}
                            id={`paint${i}_linear_${uniqueId}`}
                            x1="78.0039"
                            y1={getY1Value(i)}
                            x2="78.0039"
                            y2={getY2Value(i)}
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stopColor={colors[1]} />
                            <stop offset="0.525" stopColor={colors[0]} />
                            <stop offset="1" stopColor={colors[2]} />
                        </linearGradient>
                    ))}
                </defs>
            </svg>
            <style>
                {`
                .text-model-gradient {
                    position: relative;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.3s ease;
                    overflow: hidden;
                }

                .text-model-gradient svg {
                    width: 100%;
                    height: 100%;
                }

                @keyframes unfoldAnimation {
                    0% { transform: scaleY(0.95); }
                    50% { transform: scaleY(1.05); }
                    100% { transform: scaleY(0.95); }
                }

                .text-model-gradient-animate-hover:hover svg g {
                    animation: unfoldAnimation 2s ease-in-out infinite;
                    transform-origin: center;
                }

                .text-model-gradient-animate-always svg g {
                    animation: unfoldAnimation 2s ease-in-out infinite;
                    transform-origin: center;
                }

                .text-model-gradient-animate-hover:hover {
                    cursor: pointer;
                }
                `}
            </style>
        </div>
    );
}
